import {
  BugOutlined,
  CommentOutlined,
  DashboardOutlined,
  ExperimentOutlined,
  InboxOutlined,
  KeyOutlined,
  RobotOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Drawer,
  Form,
  Input,
  Menu,
  notification,
  Select,
  Upload,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import logo from '../assets/Brand Logo.svg';
import smallLogo from '../assets/logo_only.png';
import '../assets/Style/navbar.css';

import { fetchOrganizationsRequest } from '../redux-saga/actions/organizationAction';
import { fetchSwitchOrganizationsRequest } from '../redux-saga/actions/switchOrganizationAction';

const { Dragger } = Upload;
const { Option } = Select;

const NavBar = () => {
  const dispatch = useDispatch();
  const [collapsed, setCollapsed] = useState(false);
  const role = useSelector((state) => state.auth.role);
  const user = useSelector((state) => state.auth.userInfo);
  const location = useLocation();
  const [form] = Form.useForm();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const organizationData = useSelector(
    (state) => state.organization.organizations,
  )
  const currentOrgId = useSelector((state) => state.auth.orgId);
  useEffect(() => {
    setSelectedOrganization(currentOrgId);
  }, [currentOrgId]);
    useEffect(() => {
    if(role === 'SUPERADMIN'){
      dispatch(fetchOrganizationsRequest());
    }
  }, [dispatch]);

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('selectedOrg');
    window.location.href = '/login';
  };

  const showDrawer = () => setDrawerVisible(true);
  const closeDrawer = () => setDrawerVisible(false);

  const handleFinish = (values) => {
    notification.success({
      message: 'Success',
      description: 'Profile updated successfully.',
    });
    closeDrawer();
  };

  useEffect(() => {
    const savedOrg = localStorage.getItem('selectedOrg');
    if (savedOrg) setSelectedOrganization(savedOrg);
  }, []);

  const handleOrgChange = async (orgId) => {
    console.log('Selected Organization ID:', orgId);
    setSelectedOrganization(orgId);

    try {
      if (orgId !== 'all') {
        dispatch(fetchSwitchOrganizationsRequest({ orgId }));
      }
    } catch (error) {
      console.error('Error switching organizations:', error);
      notification.error({
        message: 'Error',
        description: 'Organization switching failed.',
      });
    }
  };

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.startsWith('/agents')) return '/agents';
    if (path.startsWith('/prompts')) return '/prompts';
    if (path.startsWith('/testcases')) return '/testcases';
    if (path.startsWith('/process')) return '/process';
    if (path.startsWith('/apikeys')) return '/apikeys';
    if (path.startsWith('/config')) return '/config';
    return '/';
  };

  const selectedKey = getSelectedKey();

  const items = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: <Link to="/">Dashboard</Link>,
    },
    {
      key: '/agents',
      icon: <RobotOutlined />,
      label: <Link to="/agents">Agents</Link>,
    },
    {
      key: '/prompts',
      icon: <CommentOutlined />,
      label: <Link to="/prompts">Prompts</Link>,
    },
    {
      key: '/testcases',
      icon: <BugOutlined />,
      label: <Link to="/testcases">Test Cases</Link>,
    },
    {
      key: '/process',
      icon: <ExperimentOutlined />,
      label: <Link to="/process">Process Cases</Link>,
    },
    {
      key: '/apikeys',
      icon: <KeyOutlined />,
      label: <Link to="/apikeys">API Keys</Link>,
    },
    {
      key: '/config',
      icon: <ToolOutlined />,
      label: <Link to="/config">Configuration</Link>,
    },
  ];

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: collapsed ? 80 : 200,
        transition: 'width 0.2s',
        backgroundColor: '#fff',
        borderRight: '1px solid #e8e8e8',
      }}
    >
      {/* Logo */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '18px',
        }}
      >
        <img
          src={collapsed ? smallLogo : logo}
          alt="Logo"
          style={{
            width: '100%',
            height: 'auto',
            maxWidth: collapsed ? '60px' : '150px',
            transition: 'all 0.3s ease',
          }}
        />
      </div>

      {/* Sidebar Menu */}
      <Menu
        defaultSelectedKeys={[selectedKey]}
        selectedKeys={[selectedKey]}
        mode="inline"
        inlineCollapsed={collapsed}
        items={items}
        style={{ flex: 1 }}
      />

      {/* Top-right section (Profile, Organization Selector, Logout) */}
      <div
        style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          zIndex: 1000,
        }}
      >
        {
          role === 'SUPERADMIN' && <Select
            value={selectedOrganization}
            onChange={handleOrgChange}
            style={{ width: 180 }}
            placeholder="Select Organization"
          >
            {organizationData?.map((organization) => (
              <Option key={organization.id} value={organization.id}>
                {organization.name}
              </Option>
            ))}
          </Select>
        }

        {/* User Profile */}
        <div
          onClick={showDrawer}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            cursor: 'pointer',
          }}
        >
          <UserOutlined />
          <span>{user?.name || 'N/A'}</span>
        </div>

        {/* Logout Button */}
        <Button type="link" onClick={logout}>
          Logout
        </Button>
      </div>

      {/* Profile Drawer */}
      <Drawer
        title="User Profile"
        placement="right"
        closable
        onClose={closeDrawer}
        visible={drawerVisible}
        width={400}
      >
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            name="username"
            label="Username"
            initialValue={user?.name || 'Username'}
          >
            <Input />
          </Form.Item>
          <Form.Item name="upload" label="Upload">
            <Dragger
              multiple={false}
              beforeUpload={() => false}
              showUploadList={false}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to upload</p>
            </Dragger>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default NavBar;
